/**
 * camera.ts — 공장 CCTV 중계(go2rtc + Caddy) 클라이언트 설정.
 *
 * 영상 경로: DVR(Hikvision) → 중계 PC(go2rtc) → Caddy(HTTPS+토큰) → 브라우저.
 * 시청 토큰은 JS 번들에 넣으면 로그인 없이도 노출되므로(Hosting 은 공개)
 * `system_settings/factory_camera` 문서(활성 사용자만 read)에서 내려받는다.
 * 문서 형식은 systemSettingsRepo 공용 규격 `{ value: <JSON 문자열> }`.
 *
 * 중계 쪽 스트림 이름 규칙(tools/camera/go2rtc.yaml):
 *   cam1~cam4   = 보조 스트림(저화질) — 스냅샷(frame.jpeg)·MJPEG 폴백 가능
 *   cam1hd~     = 메인 스트림(고화질) — MSE 라이브 전용, 스냅샷 변환 없음
 */

export interface CameraDef {
  /** go2rtc 스트림 이름 (보조 스트림, 스냅샷용) — 예: "cam1" */
  id: string;
  /** 고화질(메인 스트림) 이름 — 없으면 라이브도 id 로 재생 */
  hd_id?: string;
  /** 화면 표시명 — 예: "출하구 1" */
  label: string;
}

export interface CameraConfig {
  enabled: boolean;
  /** 중계 주소 — 예: "https://cctv.jgind.co.kr" (끝 슬래시 없이) */
  base_url: string;
  /** Caddy 시청 토큰 — Caddyfile 의 token= 값과 동일해야 함 */
  token: string;
  cams: CameraDef[];
  /** 스냅샷 그리드 갱신 주기(초) */
  snapshot_interval_sec: number;
}

export const DEFAULT_CAMERA_CONFIG: CameraConfig = {
  enabled: false,
  base_url: "",
  token: "",
  cams: [],
  snapshot_interval_sec: 10,
};

/** Firestore value(JSON parse 결과) → CameraConfig. 형식이 어긋나면 비활성 기본값. */
export function parseCameraConfig(v: unknown): CameraConfig {
  if (!v || typeof v !== "object" || Array.isArray(v)) return DEFAULT_CAMERA_CONFIG;
  const o = v as Record<string, unknown>;
  const cams: CameraDef[] = Array.isArray(o.cams)
    ? (o.cams as unknown[])
        .map((c): CameraDef | null => {
          if (!c || typeof c !== "object") return null;
          const cc = c as Record<string, unknown>;
          if (typeof cc.id !== "string" || !cc.id) return null;
          return {
            id: cc.id,
            hd_id: typeof cc.hd_id === "string" && cc.hd_id ? cc.hd_id : undefined,
            label: typeof cc.label === "string" && cc.label ? cc.label : cc.id,
          };
        })
        .filter((c): c is CameraDef => c !== null)
    : [];
  return {
    enabled: o.enabled === true,
    base_url: typeof o.base_url === "string" ? o.base_url.replace(/\/+$/, "") : "",
    token: typeof o.token === "string" ? o.token : "",
    cams,
    snapshot_interval_sec:
      typeof o.snapshot_interval_sec === "number" && o.snapshot_interval_sec >= 3
        ? o.snapshot_interval_sec
        : DEFAULT_CAMERA_CONFIG.snapshot_interval_sec,
  };
}

/** 설정이 실제로 영상을 띄울 수 있는 상태인가 (패널 노출 여부) */
export function cameraReady(cfg: CameraConfig): boolean {
  return cfg.enabled && !!cfg.base_url && !!cfg.token && cfg.cams.length > 0;
}

/** 스냅샷(JPEG) 주소 — bust 로 캐시 무력화(주기 갱신용) */
export function snapshotUrl(cfg: CameraConfig, camId: string, bust?: number): string {
  const b = bust ? `&_=${bust}` : "";
  return `${cfg.base_url}/api/frame.jpeg?src=${encodeURIComponent(camId)}&token=${cfg.token}${b}`;
}

/** MJPEG 연속 스트림 주소 — MSE 미지원 브라우저(iOS 구형 Safari) 폴백 */
export function mjpegUrl(cfg: CameraConfig, camId: string): string {
  return `${cfg.base_url}/api/stream.mjpeg?src=${encodeURIComponent(camId)}&token=${cfg.token}`;
}

/**
 * 네이티브 HLS 주소 — MSE 가 없는 iPhone Safari 용.
 * 첫 요청(마스터 플레이리스트)에 토큰이 실려 Caddy 가 쿠키를 발급하고,
 * 이후 세그먼트 요청은 같은 사이트(jgind.co.kr) 쿠키로 통과한다.
 */
export function hlsUrl(cfg: CameraConfig, camId: string): string {
  return `${cfg.base_url}/api/stream.m3u8?src=${encodeURIComponent(camId)}&mp4&token=${cfg.token}`;
}

/** MSE 라이브용 WebSocket 주소 (go2rtc /api/ws) */
export function mseWsUrl(cfg: CameraConfig, camId: string): string {
  const ws = cfg.base_url.replace(/^http/, "ws");
  return `${ws}/api/ws?src=${encodeURIComponent(camId)}&token=${cfg.token}`;
}

// ─────────────────────────────────────────────────────────────────────────────
//  중계 PC 원격 제어 — "우편함" 방식
//
//  영상 경로(cctv.jgind.co.kr → Caddy)로 재시작 명령을 보내면, 정작 그 Caddy 가
//  죽었을 때 명령이 도달하지 못한다(2026-09-14 장애가 정확히 그 상황).
//  그래서 Firestore 를 우편함으로 쓴다:
//
//    관리자 → relay_ops/command 에 명령 기록
//           → 중계 PC 의 watchdog 이 1분마다 확인하고 실행
//           → relay_ops/status 에 결과·건강 상태 기록
//           → ERP 가 onSnapshot 으로 수신
//
//  중계 PC 는 밖으로 나가는 연결만 쓰므로 포워딩·HTTPS 가 죽어도 명령이 닿는다.
// ─────────────────────────────────────────────────────────────────────────────

/** 중계 PC 가 스스로 판정한 상태 */
export type RelayState = "healthy" | "degraded" | "down";

export interface RelayCameraRecognition {
  /** 현재 감지 중인 차량 끝번호. 차량이 빠지면 빈 문자열 */
  current_number: string;
  current_seen_at: Date | null;
  /** 마지막으로 확정한 차량 끝번호. 현재 미감지여도 유지 */
  last_number: string;
  last_seen_at: Date | null;
}

export interface RelayRecognitionStatus {
  state: "online" | "degraded" | "error" | "setup" | "unknown";
  checked_at: Date | null;
  cam1: RelayCameraRecognition;
  cam2: RelayCameraRecognition;
}

export interface RelayStatus {
  state: RelayState;
  /** 중계 PC 가 마지막으로 확인한 시각 */
  checked_at: Date | null;
  public_ip: string;
  dns_ip: string;
  go2rtc: string;
  caddy: string;
  /** go2rtc 로컬 응답 코드("200" 이 정상) */
  local_code: string;
  /** 공인 주소 응답 코드("403"=토큰 없이 정상 차단, "0"=끊김) */
  public_code: string;
  cert_until: string;
  actions: string;
  last_command_result: string;
  last_command_at: Date | null;
  recognition: RelayRecognitionStatus;
}

export type RelayAction = "restart_caddy" | "restart_go2rtc" | "restart_all";

/** watchdog 이 남긴 결과 키 → 한국어 문구 (PowerShell 파일을 ASCII 로 유지하려고 키만 주고받는다) */
export function relayResultText(key: string): string {
  switch (key) {
    case "restarted_caddy":
      return "중계 서버(Caddy)를 재시작했습니다.";
    case "restarted_go2rtc":
      return "영상 중계(go2rtc)를 재시작했습니다.";
    case "restarted_all":
      return "영상 중계와 중계 서버를 모두 재시작했습니다.";
    default:
      return key.startsWith("unknown_action:") ? "알 수 없는 명령이었습니다." : key;
  }
}

/**
 * 중계 PC 가 보고를 멈춘 지 오래면 상태값 자체를 믿을 수 없다.
 * watchdog 은 최소 5분에 한 번 보고하므로 12분을 끊김 기준으로 둔다.
 */
export const RELAY_STALE_MS = 12 * 60 * 1000;

/** 중계 PC 인식은 1분 주기로 Firestore에 반영된다. 두 주기를 넘기면 현재값 대신 마지막값을 쓴다. */
export const RECOGNITION_STALE_MS = 2 * 60 * 1000;

export function recognitionValueText(
  recognition: RelayRecognitionStatus,
  camera: 1 | 2,
  now = Date.now(),
): string {
  const item = camera === 1 ? recognition.cam1 : recognition.cam2;
  const fresh = recognition.checked_at
    && now >= recognition.checked_at.getTime()
    && now - recognition.checked_at.getTime() <= RECOGNITION_STALE_MS;
  if (fresh && /^\d{4}$/.test(item.current_number)) return item.current_number;
  if (!/^\d{4}$/.test(item.last_number) || !item.last_seen_at) return "-";

  const elapsedMs = Math.max(0, now - item.last_seen_at.getTime());
  const elapsedMinutes = Math.floor(elapsedMs / 60000);
  const elapsed = elapsedMinutes < 1440
    ? `${elapsedMinutes}분 전`
    : `${Math.floor(elapsedMinutes / 1440)}일 전`;
  return `마지막 인식 ${item.last_number} (${elapsed})`;
}

export function isRelayStale(s: RelayStatus | null, now = Date.now()): boolean {
  if (!s?.checked_at) return true;
  return now - s.checked_at.getTime() > RELAY_STALE_MS;
}

/** 화면에 띄울 한 줄 요약 — 끊김이면 상태값 대신 그 사실을 알린다 */
export function relaySummary(s: RelayStatus | null): { label: string; tone: RelayState } {
  if (!s) return { label: "중계 상태 확인 안 됨", tone: "down" };
  if (isRelayStale(s)) return { label: "중계 PC 응답 없음", tone: "down" };
  if (s.state === "healthy") return { label: "중계 정상", tone: "healthy" };
  if (s.state === "degraded") {
    // 공장 안은 되는데 바깥 경로만 끊긴 경우가 대부분 — 재시작이 듣는 상황
    return { label: "바깥 연결 끊김", tone: "degraded" };
  }
  return { label: "중계 끊김", tone: "down" };
}
